import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { BackArrowLeft } from "@dreamhub/designsystem/icons";
import LayoutTabs from "@/components/controls/LayoutTabs";
import WorkflowDetailsForm from "./WorkflowDetailsForm";
import { WorkflowDetailsFormData, WorkflowSelectOption } from "./types";

export interface TabContentHelpers {
  selectType: (defaultWorkflowType: string) => void;
  close: () => void;
}

interface AddWorkflowModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stageId?: string;
  defaultTab: string;
  tabOptions: Record<string, string>;
  renderTabContent: (
    activeTab: string,
    helpers: TabContentHelpers
  ) => React.ReactNode;
  workflowTypeOptions: WorkflowSelectOption[];
  stageOptions: WorkflowSelectOption[];
  onSubmit: (data: WorkflowDetailsFormData) => Promise<void> | void;
  isSubmitting?: boolean;
}

const AddWorkflowModal: React.FC<AddWorkflowModalProps> = ({
  isOpen,
  setIsOpen,
  stageId,
  defaultTab,
  tabOptions,
  renderTabContent,
  workflowTypeOptions,
  stageOptions,
  onSubmit,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<WorkflowDetailsFormData | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab(defaultTab);
      setSelectedType(null);
      setIsFormValid(false);
      setFormData(null);
    }
  }, [isOpen, defaultTab]);

  const handleCancel = () => setIsOpen(false);

  const handleBack = () => {
    setSelectedType(null);
    setIsFormValid(false);
    setFormData(null);
  };

  const showForm = selectedType !== null;

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      headerClassname={showForm ? undefined : "border-none"}
      contentClassname="w-[1024px] max-w-[90vw] h-[840px] max-lg-mid:h-[600px] max-lg:h-[600px] flex flex-col"
      contentContainerClassname="flex-1 min-h-0"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {showForm
            ? t("workflows.modal.workflowDetails")
            : t("addWorkflow")}
        </Text>
      }
      content={
        <div className="w-full h-full flex flex-col">
          {showForm && selectedType ? (
            <div className="overflow-y-auto h-full">
              <WorkflowDetailsForm
                defaultWorkflowType={selectedType}
                workflowTypeOptions={workflowTypeOptions}
                stageOptions={stageOptions}
                stageId={stageId}
                onFormValidation={(valid, data) => {
                  setIsFormValid(valid);
                  setFormData(data);
                }}
                onSubmit={onSubmit}
              />
            </div>
          ) : (
            <>
              <div className="flex-shrink-0">
                <LayoutTabs
                  options={tabOptions}
                  defaultValue={activeTab}
                  valueSelected={setActiveTab}
                  size="lg"
                />
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {renderTabContent(activeTab, {
                  selectType: setSelectedType,
                  close: handleCancel
                })}
              </div>
            </>
          )}
        </div>
      }
      footer={
        showForm ? (
          <div className="flex justify-between items-center">
            <Button
              variant="tertiary"
              onClick={handleBack}
              className="flex gap-2"
            >
              <BackArrowLeft />
              {t("back")}
            </Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleCancel}>
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={() => formData && onSubmit(formData)}
                disabled={!isFormValid || isSubmitting}
              >
                {t("addWorkflow")}
              </Button>
            </div>
          </div>
        ) : undefined
      }
      hideDivider={!showForm}
    />
  );
};

export default AddWorkflowModal;
